import React, { useEffect, useMemo, useState } from "react"
import { Icon } from "react-icons-kit"
import { compass } from "react-icons-kit/fa/compass"
import { film } from "react-icons-kit/fa/film"
import { code } from "react-icons-kit/fa/code"
import { videoCamera } from "react-icons-kit/fa/videoCamera"
import { shoppingCart } from "react-icons-kit/fa/shoppingCart"
import { userSecret } from "react-icons-kit/fa/userSecret"
import { lightbulbO } from "react-icons-kit/fa/lightbulbO"
import { gamepad } from "react-icons-kit/fa/gamepad"
import { cogs } from "react-icons-kit/fa/cogs"
import { binoculars } from "react-icons-kit/fa/binoculars"
import { users } from "react-icons-kit/fa/users"
import { chevronLeft } from "react-icons-kit/fa/chevronLeft"
import { chevronRight } from "react-icons-kit/fa/chevronRight"
import { Box } from "theme-ui"

import FadeInUp from "../animate/FadeInUp"
import useLang from "../../hooks/useLang"
import "./style.css"

const icons = {
  binoculars,
  cogs,
  film,
  code,
  videoCamera,
  shoppingCart,
  userSecret,
  lightbulbO,
  gamepad,
  compass,
  users,
}

const Services = ({ list = [] }) => {
  const [active, setActive] = useState(0)
  const [displayIndex, setDisplayIndex] = useState(0)
  const [incomingIndex, setIncomingIndex] = useState(null)
  const [direction, setDirection] = useState("next")
  const [isAnimating, setIsAnimating] = useState(false)
  const [showBubble, setShowBubble] = useState(true)

  const isAr = useLang() === "ar"

  const content = useMemo(
    () => ({
      badge: isAr ? "حلولنا الأساسية" : "Our Core Capabilities",
      title: isAr ? "كيف ندعم نجاح أعمالك" : "How We Support Your Business",
      label: isAr ? "الخدمة" : "Service",
      previous: isAr ? "السابق" : "Previous",
      next: isAr ? "التالي" : "Next",
    }),
    [isAr]
  )

  useEffect(() => {
    setDisplayIndex(0)
    setActive(0)
    setIncomingIndex(null)
    setIsAnimating(false)
    setShowBubble(true)
  }, [])

  const changeSlide = newIndex => {
    if (isAnimating || !list?.length) return

    const normalized =
      newIndex < 0 ? list.length - 1 : newIndex >= list.length ? 0 : newIndex

    if (normalized === displayIndex) return

    const movingForward = normalized > displayIndex
    const wrapForward = displayIndex === list.length - 1 && normalized === 0
    const wrapBackward = displayIndex === 0 && normalized === list.length - 1

    let nextDirection = "next"
    if (wrapBackward) nextDirection = "prev"
    else if (wrapForward) nextDirection = "next"
    else nextDirection = movingForward ? "next" : "prev"

    setDirection(nextDirection)
    setIncomingIndex(normalized)
    setActive(normalized)
    setShowBubble(false)
    setIsAnimating(true)

    setTimeout(() => {
      setDisplayIndex(normalized)
      setIncomingIndex(null)
      setIsAnimating(false)
      
      requestAnimationFrame(() => {
        setTimeout(() => {
          setShowBubble(true)
        }, 100)
      })
    }, 600)
  }

  const currentItem = list?.[displayIndex]
  const incomingItem =
    incomingIndex !== null && incomingIndex !== undefined
      ? list?.[incomingIndex]
      : null

  const getServiceNumber = item => {
    if (!item || !list?.length) return "01"
    const index = list.findIndex(x => x?.title === item?.title)
    return String(index >= 0 ? index + 1 : 1).padStart(2, "0")
  }

  const renderPanel = (item, isIncoming = false) => {
    const itemIcon = icons[item?.icon] || users
    const isCurrentActive = !isIncoming && active === displayIndex

    return (
      <div className="services-modern__panel-grid">
        <div className="services-modern__panel-main">
          <div className="services-modern__grid-lines" />

          <div className="services-modern__panel-top">
            <span className="services-modern__panel-icon">
              <Icon size={34} icon={itemIcon} />
            </span>

            <div className="services-modern__panel-heading">
              <span className="services-modern__eyebrow">{content.label}</span>
              <h3 className="services-modern__panel-title">{item?.title}</h3>
            </div>
          </div>

          <p className="services-modern__description">{item?.description}</p>

          <div className={`services-modern__panel-side ${isCurrentActive && showBubble ? "is-visible" : ""}`}>
            <span className="services-modern__count">
              {getServiceNumber(item)}
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (!list || list.length === 0) {
    return null
  }

  return (
    <section
      className={`services-modern ${isAr ? "services-modern-ar" : ""}`}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div className="container">
        <div className="services-modern__header text-center">
          <FadeInUp>
            <Box className="about-badge-wrapper">
              <span className="about-badge">
                <span className="about-badge-dot" />
                {content.badge}
              </span>
            </Box>
          </FadeInUp>

          <FadeInUp>
            <h2 className="services-modern__title">{content.title}</h2>
          </FadeInUp>
        </div>

        <FadeInUp>
          <div className="services-modern__tabs" role="tablist" aria-label={content.title}>
            {list.map(({ icon, title }, index) => {
              const iconComponent = icons[icon] || users
              const isActive = active === index

              return (
                <button
                  key={`${title}-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={title}
                  className={`services-modern__tab ${isActive ? "is-active" : ""}`}
                  onClick={() => changeSlide(index)}
                >
                  <span className="services-modern__tab-icon">
                    <Icon size={20} icon={iconComponent} />
                  </span>
                  <span className="services-modern__tab-text">{title}</span>
                </button>
              )
            })}
          </div>
        </FadeInUp>

        <FadeInUp>
          <div className="services-modern__slider-shell">
            <button
              type="button"
              className="services-modern__nav services-modern__nav--prev"
              aria-label={content.previous}
              onClick={() => changeSlide(displayIndex - 1)}
            >
              <Icon size={16} icon={chevronLeft} />
            </button>

            <div className="services-modern__slider-window">
              <div
                className={`services-modern__panel services-modern__panel--current ${
                  isAnimating
                    ? direction === "next"
                      ? "is-exit-left"
                      : "is-exit-right"
                    : "is-active"
                }`}
              >
                {currentItem && renderPanel(currentItem)}
              </div>

              {incomingItem && (
                <div
                  className={`services-modern__panel services-modern__panel--incoming ${
                    direction === "next" ? "is-enter-right" : "is-enter-left"
                  }`}
                >
                  {renderPanel(incomingItem, true)}
                </div>
              )}
            </div>

            <button
              type="button"
              className="services-modern__nav services-modern__nav--next"
              aria-label={content.next}
              onClick={() => changeSlide(displayIndex + 1)}
            >
              <Icon size={16} icon={chevronRight} />
            </button>
          </div>
        </FadeInUp>

        <FadeInUp>
          <div className="services-modern__pagination" aria-label="Slider pagination">
            {list.map((item, index) => (
              <button
                key={`${item.title}-dot-${index}`}
                type="button"
                className={`services-modern__dot ${
                  active === index ? "is-active" : ""
                }`}
                aria-label={`${item.title} dot`}
                onClick={() => changeSlide(index)}
              />
            ))}
          </div>
        </FadeInUp>
      </div>
    </section>
  )
}

export default Services